import {
  DEFAULT_TRANSCRIPTION_MODEL,
  TRANSCRIPTION_MODELS,
  TRANSCRIPTION_MODEL_IDS,
  getProcessingMode,
  getTranscriptionModelInfo,
  getTranscriptionProviderType,
  isValidTranscriptionModel,
  modelSupportsDiarization,
  normalizeTranscriptionText
} from "./chunk-EURN7W3L.js";
import {
  tesselUrls
} from "./chunk-OPLDAE5O.js";
import {
  KV_TTL,
  kvKeys
} from "./chunk-UNNFTBLN.js";
import {
  blockNoteToMarkdown,
  combineFrontmatterAndContent,
  generateFrontmatter,
  hasFrontmatter,
  markdownToBlockNote,
  parseFrontmatter,
  removeFrontmatter,
  updateFrontmatter
} from "./chunk-G5Y55BIW.js";
import {
  EXTRACTABLE_NOTE_TYPES,
  NOTE_CATEGORIES,
  NOTE_CATEGORIES_CONFIG,
  NOTE_TYPES,
  getCategoryConfig,
  getSystemCategoriesForPrompt,
  getSystemCategoriesWithDescriptions,
  isExtractableNoteType,
  normalizeCategory,
  normalizeNoteType
} from "./chunk-6HH4ASIL.js";
import {
  NOTE_REF_PATTERN,
  countMentions,
  createMention,
  hasMentions,
  hasNoteRefs,
  parseNoteRefs,
  parseTextMentions,
  splitTextByRefs,
  stripMentions
} from "./chunk-EDWZ3WFI.js";
import {
  DEFAULT_RELATION_TYPE,
  RELATION_TYPES,
  isRelationType
} from "./chunk-A44GLQT4.js";
import {
  createEmptyBlockNoteContent
} from "./chunk-WEC5ML6M.js";
import {
  CF_METRIC_DISPLAY_NAMES,
  CF_PRICING,
  CF_SERVICE_DISPLAY_NAMES,
  CF_SERVICE_KEYS,
  R2_CLASS_A_ACTIONS,
  computeMetricCost,
  computeServiceCost,
  isCfServiceKey
} from "./chunk-7ZE6NJDK.js";
import {
  cn
} from "./chunk-RHUPFALB.js";
import {
  formatDate,
  formatDateForInput,
  formatDateToLocale,
  formatRelativeTime
} from "./chunk-JH7IR64L.js";
import {
  Logger,
  createLogger,
  defaultLogger
} from "./chunk-R2PM4C72.js";
import {
  ALL_SUPPORTED_TYPES,
  FILE_PROCESSING_STATUS,
  FILE_TYPE_CATEGORIES,
  FileSecurityValidator,
  SECURITY_VALIDATIONS,
  SUPPORTED_AUDIO_TYPES,
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_VIDEO_TYPES,
  UPLOAD_CONFIG,
  UPLOAD_LIMITS,
  formatFileSize,
  getAcceptAttribute,
  getFileTypeCategory,
  getFileTypeName,
  validateFileMimeType,
  validateFileSize
} from "./chunk-HMKEFZ6C.js";
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  SUPPORTED_LANGUAGES
} from "./chunk-CAPATJ24.js";
import {
  CLOUDFLARE_FALLBACK_MODEL,
  DEFAULT_IMAGE_GENERATION_MODEL,
  IMAGE_GENERATION_GROUPS,
  IMAGE_GENERATION_GROUP_IDS,
  IMAGE_GENERATION_MODELS,
  IMAGE_GENERATION_MODEL_IDS,
  IMAGE_MODEL_ID,
  OPENAI_MODEL_QUALITY_MAP,
  PROVIDER_DEFAULT_MODEL,
  getGroupDefaultModel,
  getGroupForModel,
  getImageGenerationModelInfo,
  getImageProviderType,
  isValidImageGenerationModel
} from "./chunk-TWPIXBC3.js";

// src/constants.ts
import {
  DEFAULT_LOCALE,
  isValidLocale,
  LOCALE_COOKIE_NAME,
  LOCALE_FLAGS,
  LOCALE_NAMES,
  SUPPORTED_LOCALES
} from "@tessel/i18n";
var DEFAULT_FOLDER_IDS = {
  ANNOTATIONS: "annotations",
  EVENT: "event",
  SESSION: "sessions",
  SUMMARIES: "summaries",
  CHARACTERS: "characters",
  LOCATIONS: "locations",
  ITEMS: "items",
  TRASH: "trash"
};
var DEFAULT_TIMEZONE = "America/Sao_Paulo";

// src/hooks/useMobile.tsx
import { useEffect, useState } from "react";
var MOBILE_BREAKPOINT = 768;
var getInitialMobileState = () => {
  if (typeof globalThis === "undefined" || !("window" in globalThis)) {
    return void 0;
  }
  return globalThis.window.innerWidth < MOBILE_BREAKPOINT;
};
var useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(getInitialMobileState);
  useEffect(() => {
    if (typeof globalThis === "undefined" || !("window" in globalThis)) {
      return;
    }
    const win = globalThis.window;
    const mql = win.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(win.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(win.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return Boolean(isMobile);
};

// src/hooks/useScrollToBottom.ts
import { useCallback, useEffect as useEffect2, useRef } from "react";
var useScrollToBottom = (options = {}) => {
  const { behavior = "smooth", dependencies = [] } = options;
  const bottomRef = useRef(null);
  const scrollToBottom = useCallback(() => {
    if (bottomRef.current && "scrollIntoView" in bottomRef.current) {
      ;
      bottomRef.current.scrollIntoView({ behavior });
    }
  }, [behavior]);
  useEffect2(() => {
    scrollToBottom();
  }, [scrollToBottom, ...dependencies]);
  return {
    bottomRef,
    scrollToBottom
  };
};

// src/json-parser.ts
function cleanMarkdownJSON(text) {
  let cleanText = text.trim();
  if (cleanText.startsWith("```json") && cleanText.endsWith("```")) {
    cleanText = cleanText.replace(/^```json\s*\n?/, "").replace(/\n?\s*```$/, "");
  } else if (cleanText.startsWith("```") && cleanText.endsWith("```")) {
    cleanText = cleanText.replace(/^```\s*\n?/, "").replace(/\n?\s*```$/, "");
  }
  return cleanText.trim();
}
function parseJSONResponse(text) {
  const cleanText = cleanMarkdownJSON(text);
  return JSON.parse(cleanText);
}
function safeParseJSONResponse(text) {
  try {
    return parseJSONResponse(text);
  } catch {
    return null;
  }
}
function extractJSONFromText(text) {
  const cleaned = cleanMarkdownJSON(text);
  try {
    return JSON.parse(cleaned);
  } catch {
  }
  const objectMatch = text.match(/\{[\s\S]*\}/);
  if (objectMatch) {
    try {
      return JSON.parse(objectMatch[0]);
    } catch {
    }
  }
  const arrayMatch = text.match(/\[[\s\S]*\]/);
  if (arrayMatch) {
    try {
      return JSON.parse(arrayMatch[0]);
    } catch {
    }
  }
  return null;
}

// src/language-service.ts
var logger = createLogger({ prefix: "LanguageService" });
var WORKER_LANGUAGE_INSTRUCTIONS = {
  [LANGUAGES.EN]: "Generate all event names, descriptions, and outputs in English. Use clear, concise language appropriate for tabletop RPG content.",
  [LANGUAGES.PT_BR]: "Gere todos os nomes de eventos, descri\xE7\xF5es e sa\xEDdas em Portugu\xEAs Brasileiro. Use linguagem clara e concisa apropriada para conte\xFAdo de RPG de mesa.",
  [LANGUAGES.ES]: "Genera todos los nombres de eventos, descripciones y salidas en Espa\xF1ol. Usa lenguaje claro y conciso apropiado para contenido de juegos de rol de mesa."
};
var RAG_LANGUAGE_INSTRUCTIONS = {
  [LANGUAGES.PT_BR]: "Responda sempre em Portugu\xEAs Brasileiro. Use linguagem clara e natural apropriada para jogos de RPG de mesa.",
  [LANGUAGES.EN]: "Always respond in English. Use clear and natural language appropriate for tabletop RPG games.",
  [LANGUAGES.ES]: "Responde siempre en Espa\xF1ol. Usa lenguaje claro y natural apropiado para juegos de rol de mesa."
};
var LanguageService = class {
  /**
   * Validate and normalize language input
   */
  validateLanguage(language) {
    logger.debug("\u{1F50D} Language validation started", {
      provided: language,
      type: typeof language,
      isNull: language === null,
      isUndefined: language === void 0
    });
    if (!language) {
      logger.debug("\u{1F50D} No language provided, using default", {
        default: DEFAULT_LANGUAGE
      });
      return DEFAULT_LANGUAGE;
    }
    const normalizedLanguage = language.toLowerCase().trim();
    logger.debug("\u{1F50D} Language normalized", {
      original: language,
      normalized: normalizedLanguage
    });
    if (Object.keys(SUPPORTED_LANGUAGES).includes(normalizedLanguage)) {
      logger.debug("\u{1F50D} Found direct match", { normalizedLanguage });
      return normalizedLanguage;
    }
    const languageMap = {
      pt: LANGUAGES.PT_BR,
      portuguese: LANGUAGES.PT_BR,
      portugu\u00EAs: LANGUAGES.PT_BR,
      spanish: LANGUAGES.ES,
      espa\u00F1ol: LANGUAGES.ES,
      english: LANGUAGES.EN,
      ingl\u00EAs: LANGUAGES.EN
    };
    if (languageMap[normalizedLanguage]) {
      logger.debug("\u{1F50D} Found mapped variation", {
        normalized: normalizedLanguage,
        mapped: languageMap[normalizedLanguage]
      });
      return languageMap[normalizedLanguage];
    }
    logger.warn("Unknown language provided, using default", {
      provided: language,
      normalized: normalizedLanguage,
      default: DEFAULT_LANGUAGE
    });
    return DEFAULT_LANGUAGE;
  }
  /**
   * Get language instruction for AI workers (fragment extraction, etc.)
   */
  getLanguageInstruction(language) {
    const validatedLanguage = this.validateLanguage(language);
    return WORKER_LANGUAGE_INSTRUCTIONS[validatedLanguage];
  }
  /**
   * Get language instruction for RAG/chat responses
   */
  getRagLanguageInstruction(language) {
    const validatedLanguage = this.validateLanguage(language);
    return RAG_LANGUAGE_INSTRUCTIONS[validatedLanguage];
  }
  /**
   * Get language from context object
   */
  getCampaignLanguageFromContext(context) {
    const campaignLanguage = context?.campaignLanguage;
    return this.validateLanguage(
      typeof campaignLanguage === "string" ? campaignLanguage : null
    );
  }
  /**
   * Get user-friendly language display name
   */
  getLanguageDisplayName(language) {
    const displayNames = {
      [LANGUAGES.EN]: "English",
      [LANGUAGES.PT_BR]: "Portugu\xEAs (Brasil)",
      [LANGUAGES.ES]: "Espa\xF1ol"
    };
    return displayNames[language] ?? displayNames[DEFAULT_LANGUAGE];
  }
  /**
   * Check if a string is a valid supported language
   */
  isValidLanguage(language) {
    return Object.keys(SUPPORTED_LANGUAGES).includes(language);
  }
  /**
   * Get all supported languages with display names
   */
  getSupportedLanguages() {
    return Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => ({
      code,
      name,
      displayName: this.getLanguageDisplayName(code)
    }));
  }
  /**
   * Detect language from text content (basic heuristics)
   */
  detectLanguage(text) {
    if (!text || text.length < 10) {
      return DEFAULT_LANGUAGE;
    }
    const lowerText = text.toLowerCase();
    const ptIndicators = [
      "\xE7\xE3o",
      "ss\xE3o",
      "\xF5es",
      "\xE3",
      "\xF5",
      "\xEA",
      "\xE1",
      "\xE9",
      "\xED",
      "\xF3",
      "\xFA",
      "de",
      "da",
      "do",
      "das",
      "dos",
      "para",
      "com",
      "por",
      "em",
      "no",
      "na",
      "que",
      "n\xE3o",
      "um",
      "uma",
      "s\xE3o",
      "est\xE1",
      "foi",
      "ter",
      "seu",
      "sua"
    ];
    const esIndicators = [
      "ci\xF3n",
      "si\xF3n",
      "\xF1",
      "ll",
      "rr",
      "que",
      "con",
      "por",
      "para",
      "del",
      "las",
      "los",
      "una",
      "uno",
      "son",
      "est\xE1",
      "fue",
      "tener",
      "su",
      "sus"
    ];
    const enIndicators = [
      "the",
      "and",
      "or",
      "but",
      "in",
      "on",
      "at",
      "to",
      "for",
      "of",
      "with",
      "by",
      "from",
      "up",
      "about",
      "into",
      "through",
      "during",
      "before",
      "after"
    ];
    let ptScore = 0;
    let esScore = 0;
    let enScore = 0;
    ptIndicators.forEach((indicator) => {
      if (lowerText.includes(indicator)) {
        ptScore++;
      }
    });
    esIndicators.forEach((indicator) => {
      if (lowerText.includes(indicator)) {
        esScore++;
      }
    });
    enIndicators.forEach((indicator) => {
      if (lowerText.includes(indicator)) {
        enScore++;
      }
    });
    if (ptScore > esScore && ptScore > enScore) {
      return LANGUAGES.PT_BR;
    } else if (esScore > enScore) {
      return LANGUAGES.ES;
    } else if (enScore > 0) {
      return LANGUAGES.EN;
    }
    return DEFAULT_LANGUAGE;
  }
  /**
   * Get language-specific formatting rules
   */
  getFormattingRules(language) {
    const rules = {
      [LANGUAGES.PT_BR]: {
        dateFormat: "DD/MM/YYYY",
        timeFormat: "HH:mm",
        numberFormat: "1.234,56",
        currency: "BRL"
      },
      [LANGUAGES.EN]: {
        dateFormat: "MM/DD/YYYY",
        timeFormat: "hh:mm AM/PM",
        numberFormat: "1,234.56",
        currency: "USD"
      },
      [LANGUAGES.ES]: {
        dateFormat: "DD/MM/YYYY",
        timeFormat: "HH:mm",
        numberFormat: "1.234,56",
        currency: "EUR"
      }
    };
    return rules[language];
  }
  /**
   * Get language direction (for UI layout)
   */
  getLanguageDirection(_language) {
    return "ltr";
  }
  /**
   * Get language-specific stopwords for text processing
   */
  getStopwords(language) {
    const stopwords = {
      [LANGUAGES.PT_BR]: [
        "a",
        "o",
        "e",
        "\xE9",
        "de",
        "do",
        "da",
        "em",
        "um",
        "uma",
        "para",
        "com",
        "n\xE3o",
        "que",
        "se",
        "na",
        "por",
        "mais",
        "as",
        "os",
        "como",
        "mas",
        "foi",
        "ao",
        "ele",
        "das",
        "tem",
        "\xE0",
        "seu",
        "sua",
        "ou",
        "ser",
        "quando",
        "muito",
        "h\xE1",
        "nos",
        "j\xE1",
        "est\xE1",
        "eu",
        "tamb\xE9m",
        "s\xF3",
        "pelo",
        "pela",
        "at\xE9",
        "isso",
        "ela",
        "entre",
        "era",
        "depois",
        "sem",
        "mesmo",
        "aos",
        "ter",
        "seus",
        "suas",
        "numa",
        "nem"
      ],
      [LANGUAGES.EN]: [
        "a",
        "an",
        "and",
        "are",
        "as",
        "at",
        "be",
        "by",
        "for",
        "from",
        "has",
        "he",
        "in",
        "is",
        "it",
        "its",
        "of",
        "on",
        "that",
        "the",
        "to",
        "was",
        "will",
        "with",
        "this",
        "but",
        "they",
        "have",
        "had",
        "what",
        "said",
        "each",
        "which",
        "she",
        "do",
        "how",
        "their",
        "if",
        "up",
        "out",
        "many",
        "then",
        "them",
        "these",
        "so",
        "some",
        "her",
        "would",
        "make",
        "like",
        "into",
        "him",
        "time",
        "two",
        "more"
      ],
      [LANGUAGES.ES]: [
        "a",
        "al",
        "algo",
        "algunas",
        "algunos",
        "ante",
        "antes",
        "como",
        "con",
        "contra",
        "cual",
        "cuando",
        "de",
        "del",
        "desde",
        "donde",
        "durante",
        "e",
        "el",
        "ella",
        "ellas",
        "ellos",
        "en",
        "entre",
        "era",
        "erais",
        "eran",
        "eras",
        "eres",
        "es",
        "esa",
        "esas",
        "ese",
        "eso",
        "esos",
        "esta",
        "estaba",
        "estado",
        "estas",
        "este",
        "esto",
        "estos",
        "fue",
        "fueron",
        "ha",
        "hace",
        "haciendo",
        "hasta",
        "hay",
        "la",
        "las",
        "le",
        "les",
        "lo",
        "los",
        "me",
        "mi",
        "mis",
        "mucho",
        "muy",
        "nada",
        "ni",
        "no",
        "nos",
        "nosotras",
        "nosotros",
        "nuestra",
        "nuestras"
      ]
    };
    return stopwords[language] || [];
  }
};
var languageService = new LanguageService();
function getLanguageInstruction(language) {
  return languageService.getLanguageInstruction(language);
}
function getRagLanguageInstruction(language) {
  return languageService.getRagLanguageInstruction(language);
}
function getDateLocaleIdentifier(language) {
  const validatedLanguage = languageService.validateLanguage(language);
  const localeMap = {
    [LANGUAGES.PT_BR]: "pt-BR",
    [LANGUAGES.EN]: "en-US",
    [LANGUAGES.ES]: "es"
  };
  return localeMap[validatedLanguage];
}

// src/supabase/client.ts
import { createClient } from "@supabase/supabase-js";
var cachedClient = null;
var cachedEnvKey = null;
function createSupabaseClient(env) {
  if (!(env.SUPABASE_URL && env.SUPABASE_SERVICE_ROLE_KEY)) {
    throw new Error(
      "Supabase: SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY are required"
    );
  }
  const envKey = `${env.SUPABASE_URL}:${env.SUPABASE_SERVICE_ROLE_KEY}`;
  if (cachedClient && cachedEnvKey === envKey) {
    return cachedClient;
  }
  const client = createClient(
    env.SUPABASE_URL,
    env.SUPABASE_SERVICE_ROLE_KEY,
    {
      auth: {
        persistSession: false,
        // Workers don't need session persistence
        autoRefreshToken: false
        // Service role doesn't need refresh
      }
    }
  );
  cachedClient = client;
  cachedEnvKey = envKey;
  return client;
}
function clearSupabaseClientCache() {
  cachedClient = null;
  cachedEnvKey = null;
}
function createUserScopedSupabaseClient(env, accessToken) {
  if (!(env.SUPABASE_URL && env.SUPABASE_ANON_KEY)) {
    throw new Error(
      "Supabase: SUPABASE_URL and SUPABASE_ANON_KEY are required for user-scoped client"
    );
  }
  return createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
    global: {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    },
    auth: {
      persistSession: false,
      autoRefreshToken: false
    }
  });
}

// src/note-context.ts
var logger2 = createLogger({ prefix: "NoteContext" });
function parseNoteContext(note) {
  const { frontmatter } = parseFrontmatter(note.markdown_content);
  return {
    id: note.id,
    name: frontmatter.name || note.name,
    type: note.type ?? "",
    frontmatter,
    data: note.data ?? null
  };
}
async function getNotesByType(supabaseUrl, supabaseKey, campaignId, type, filters) {
  const supabase = createSupabaseClient({
    SUPABASE_URL: supabaseUrl,
    SUPABASE_SERVICE_ROLE_KEY: supabaseKey
  });
  logger2.info("Fetching notes by type", { campaignId, type, filters });
  const { data, error } = await supabase.from("notes").select("id, name, type, markdown_content, data").eq("campaign_id", campaignId).eq("type", type).is("deleted_at", null);
  if (error) {
    logger2.error("Failed to fetch notes", { error: error.message, type });
    return [];
  }
  let notes = (data || []).map(parseNoteContext);
  if (filters?.status) {
    notes = notes.filter((n) => n.frontmatter.status === filters.status);
  }
  if (filters?.activeOnly) {
    notes = notes.filter(
      (n) => n.frontmatter.completed !== true && n.frontmatter.status !== "completed"
    );
  }
  logger2.info("Notes fetched", {
    campaignId,
    type,
    count: notes.length,
    names: notes.map((n) => n.name)
  });
  return notes;
}
async function getNotesByTypes(supabaseUrl, supabaseKey, campaignId, types) {
  const supabase = createSupabaseClient({
    SUPABASE_URL: supabaseUrl,
    SUPABASE_SERVICE_ROLE_KEY: supabaseKey
  });
  logger2.info("Fetching notes by types", { campaignId, types });
  const { data, error } = await supabase.from("notes").select("id, name, type, markdown_content, data").eq("campaign_id", campaignId).in("type", types).is("deleted_at", null);
  if (error) {
    logger2.error("Failed to fetch notes by types", {
      error: error.message,
      types
    });
    return [];
  }
  const notes = (data || []).map(parseNoteContext);
  logger2.info("Notes fetched by types", {
    campaignId,
    types,
    count: notes.length
  });
  return notes;
}
var LOCALIZED_STRINGS = {
  "pt-br": {
    noItems: "Nenhum item encontrado."
  },
  en: {
    noItems: "No items found."
  },
  es: {
    noItems: "No se encontraron elementos."
  }
};
async function getActiveQuests(supabaseUrl, supabaseKey, campaignId) {
  return getNotesByType(supabaseUrl, supabaseKey, campaignId, "quest", {
    activeOnly: true
  });
}
function formatQuestsForPrompt(quests, locale = "pt-br") {
  if (!quests.length) {
    const strings = LOCALIZED_STRINGS[locale] || LOCALIZED_STRINGS["pt-br"];
    return strings.noItems;
  }
  return quests.map((q) => {
    const { frontmatter } = q;
    const objective = frontmatter.current_objective || frontmatter.objective || "";
    const status = frontmatter.status || "active";
    const lines = [`- **${q.name}** [ID: ${q.id}] (${status})`];
    if (objective) {
      lines.push(`  Objetivo: ${objective}`);
    }
    const relevantFields = ["reward", "difficulty", "deadline", "giver"];
    for (const field of relevantFields) {
      if (frontmatter[field]) {
        lines.push(`  ${field}: ${frontmatter[field]}`);
      }
    }
    lines.push(`  \u2192 Use: [[ref:${q.id}|${q.name}]]`);
    return lines.join("\n");
  }).join("\n");
}
function formatNotesMinimal(notes, options) {
  const locale = options?.locale || "pt-br";
  const strings = LOCALIZED_STRINGS[locale] || LOCALIZED_STRINGS["pt-br"];
  if (!notes.length) {
    return strings.noItems;
  }
  return notes.map((n) => {
    const subtype = n.frontmatter.subtype;
    const aliasesRaw = n.data?.aliases ?? n.frontmatter.aliases;
    const aliases = Array.isArray(aliasesRaw) ? aliasesRaw.join(", ") : aliasesRaw;
    const typeTag = subtype ? `${n.type}/${subtype.toLowerCase()}` : n.type;
    const parts = [`- **${n.name}** [${typeTag}] [ID: ${n.id}]`];
    if (aliases) {
      parts.push(`| Aliases: ${aliases}`);
    }
    parts.push(`\u2192 Use: [[ref:${n.id}|${n.name}]]`);
    return parts.join(" ");
  }).join("\n");
}
function getContentWithoutFrontmatter(markdownContent) {
  return removeFrontmatter(markdownContent).trim();
}

// src/system-folders.ts
import { v5 as uuidv5 } from "uuid";
var TESSEL_SYSTEM_FOLDER_NAMESPACE = "a1b2c3d4-e5f6-7890-abcd-ef1234567890";
function getSessionsFolderId(campaignId) {
  return uuidv5(`sessions:${campaignId}`, TESSEL_SYSTEM_FOLDER_NAMESPACE);
}

// src/types/database.types.ts
var Constants = {
  public: {
    Enums: {
      page_view_type: [
        "campaign_home",
        "session",
        "note",
        "campaign",
        "characters",
        "character",
        "images",
        "palantir",
        "recommendations"
      ],
      reaction_content_type: ["session", "note", "image", "text", "comment"],
      reactions: [
        "like",
        "celebrate",
        "love",
        "funny",
        "sad",
        "party",
        "dislike"
      ],
      roles: ["admin", "editor", "moderator"]
    }
  }
};

// src/types/staged-fragments.ts
var UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var normalizeConnections = (connections) => {
  if (!connections || connections.length === 0) {
    return [];
  }
  return connections.map((conn) => {
    if (typeof conn === "string") {
      if (UUID_REGEX.test(conn)) {
        return { note_id: conn };
      }
      return { name: conn };
    }
    return conn;
  });
};

// src/types/stateful-fragments.ts
var STATEFUL_FRAGMENT_TYPES = ["quest", "item"];
function isStatefulFragmentType(type) {
  return STATEFUL_FRAGMENT_TYPES.includes(type);
}
export {
  ALL_SUPPORTED_TYPES,
  CF_METRIC_DISPLAY_NAMES,
  CF_PRICING,
  CF_SERVICE_DISPLAY_NAMES,
  CF_SERVICE_KEYS,
  CLOUDFLARE_FALLBACK_MODEL,
  Constants,
  DEFAULT_FOLDER_IDS,
  DEFAULT_IMAGE_GENERATION_MODEL,
  DEFAULT_LANGUAGE,
  DEFAULT_LOCALE,
  DEFAULT_RELATION_TYPE,
  DEFAULT_TIMEZONE,
  DEFAULT_TRANSCRIPTION_MODEL,
  EXTRACTABLE_NOTE_TYPES,
  FILE_PROCESSING_STATUS,
  FILE_TYPE_CATEGORIES,
  FileSecurityValidator,
  IMAGE_GENERATION_GROUPS,
  IMAGE_GENERATION_GROUP_IDS,
  IMAGE_GENERATION_MODELS,
  IMAGE_GENERATION_MODEL_IDS,
  IMAGE_MODEL_ID,
  KV_TTL,
  LANGUAGES,
  LOCALE_COOKIE_NAME,
  LOCALE_FLAGS,
  LOCALE_NAMES,
  LanguageService,
  Logger,
  NOTE_CATEGORIES,
  NOTE_CATEGORIES_CONFIG,
  NOTE_REF_PATTERN,
  NOTE_TYPES,
  OPENAI_MODEL_QUALITY_MAP,
  PROVIDER_DEFAULT_MODEL,
  R2_CLASS_A_ACTIONS,
  RAG_LANGUAGE_INSTRUCTIONS,
  RELATION_TYPES,
  SECURITY_VALIDATIONS,
  STATEFUL_FRAGMENT_TYPES,
  SUPPORTED_AUDIO_TYPES,
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_LANGUAGES,
  SUPPORTED_LOCALES,
  SUPPORTED_VIDEO_TYPES,
  TRANSCRIPTION_MODELS,
  TRANSCRIPTION_MODEL_IDS,
  UPLOAD_CONFIG,
  UPLOAD_LIMITS,
  WORKER_LANGUAGE_INSTRUCTIONS,
  blockNoteToMarkdown,
  cleanMarkdownJSON,
  clearSupabaseClientCache,
  cn,
  combineFrontmatterAndContent,
  computeMetricCost,
  computeServiceCost,
  countMentions,
  createEmptyBlockNoteContent,
  createLogger,
  createMention,
  createSupabaseClient,
  createUserScopedSupabaseClient,
  extractJSONFromText,
  formatDate,
  formatDateForInput,
  formatDateToLocale,
  formatFileSize,
  formatNotesMinimal,
  formatQuestsForPrompt,
  formatRelativeTime,
  generateFrontmatter,
  getAcceptAttribute,
  getActiveQuests,
  getCategoryConfig,
  getContentWithoutFrontmatter,
  getDateLocaleIdentifier,
  getFileTypeCategory,
  getFileTypeName,
  getGroupDefaultModel,
  getGroupForModel,
  getImageGenerationModelInfo,
  getImageProviderType,
  getLanguageInstruction,
  getNotesByType,
  getNotesByTypes,
  getProcessingMode,
  getRagLanguageInstruction,
  getSessionsFolderId,
  getSystemCategoriesForPrompt,
  getSystemCategoriesWithDescriptions,
  getTranscriptionModelInfo,
  getTranscriptionProviderType,
  hasFrontmatter,
  hasMentions,
  hasNoteRefs,
  isCfServiceKey,
  isExtractableNoteType,
  isRelationType,
  isStatefulFragmentType,
  isValidImageGenerationModel,
  isValidLocale,
  isValidTranscriptionModel,
  kvKeys,
  languageService,
  defaultLogger as logger,
  markdownToBlockNote,
  modelSupportsDiarization,
  normalizeCategory,
  normalizeConnections,
  normalizeNoteType,
  normalizeTranscriptionText,
  parseFrontmatter,
  parseJSONResponse,
  parseNoteContext,
  parseNoteRefs,
  parseTextMentions,
  removeFrontmatter,
  safeParseJSONResponse,
  splitTextByRefs,
  stripMentions,
  tesselUrls,
  updateFrontmatter,
  useIsMobile,
  useScrollToBottom,
  validateFileMimeType,
  validateFileSize
};
//# sourceMappingURL=index.js.map